/**
 * PremiumTabBar.js
 *
 * The floating bottom navigation shared by every mode home (management, sales,
 * driver, service, warehouse): a dark rounded pill whose ACTIVE tab expands into
 * an orange gradient capsule with icon + label, inactive tabs stay icon-only.
 * Plug it into a React Navigation v6 bottom-tab navigator with the `tabBar`
 * prop:
 *
 * ```jsx
 * <Tab.Navigator tabBar={renderPremiumTabBar} screenOptions={PREMIUM_TAB_SCREEN_OPTIONS}>
 * ```
 *
 * It honours the standard tab options (`tabBarIcon`, `tabBarLabel`,
 * `tabBarBadge`, `tabBarAccessibilityLabel`, `tabBarStyle.display`) and emits
 * the usual `tabPress` / `tabLongPress` events, so listeners such as
 * `useLastTab` keep working unchanged.
 *
 * @module components/premium/PremiumTabBar
 */

import React, {useEffect, useRef, useState} from 'react';
import {Animated, Easing, Keyboard, Platform, Pressable, StyleSheet, Text, View} from 'react-native';
import {LinearGradient} from 'expo-linear-gradient';
import {BRAND, NIGHT, useManagementTheme} from '../../helpers/managementTheme';
import {useBottomInset} from '../../helpers/safeArea';
import {withHaptic} from '../../helpers/haptics';

/** Visual height of the pill (px). Reported to the navigator through `tabBarStyle.height`. */
export const PREMIUM_TAB_BAR_HEIGHT = 68;

/** Gap between the pill and the bottom inset (px) — matches the module bar. */
export const PREMIUM_TAB_BAR_OFFSET = 10;

/**
 * `screenOptions` every premium tab navigator should merge in. The height lets
 * `BottomTabBarHeightContext` (and therefore ManagementScreen's FAB clearance)
 * know how much of the scene the floating pill covers.
 */
export const PREMIUM_TAB_SCREEN_OPTIONS = {
    headerShown: false,
    tabBarHideOnKeyboard: true,
    tabBarStyle: {position: 'absolute', height: PREMIUM_TAB_BAR_HEIGHT, backgroundColor: 'transparent', borderTopWidth: 0, elevation: 0},
};

/**
 * Resolve the text label of a tab from its options.
 *
 * @param {object} options Descriptor options.
 * @param {string} routeName Route name fallback.
 * @returns {string} Plain label.
 */
const resolveLabel = (options, routeName) => {
    if (typeof options.tabBarLabel === 'string') {
        return options.tabBarLabel;
    }
    if (typeof options.title === 'string') {
        return options.title;
    }

    return routeName;
};

/**
 * One tab: icon-only when idle, orange capsule with label when focused.
 *
 * @param {object} props
 * @param {boolean} props.focused Whether this tab is active.
 * @param {string} props.label Tab label.
 * @param {Function} props.renderIcon `(color) => JSX.Element`.
 * @param {number|string} [props.badge] Badge content.
 * @param {Function} props.onPress Press handler.
 * @param {Function} [props.onLongPress] Long-press handler.
 * @param {string} [props.accessibilityLabel] Screen-reader label.
 * @param {string} props.inactiveColor Icon colour while idle.
 * @returns {JSX.Element} The tab.
 */
const TabItem = ({focused, label, renderIcon, badge, onPress, onLongPress, accessibilityLabel, inactiveColor}) => {
    const grow = useRef(new Animated.Value(focused ? 1 : 0)).current;

    useEffect(() => {
        Animated.timing(grow, {toValue: focused ? 1 : 0, duration: 240, easing: Easing.out(Easing.cubic), useNativeDriver: false}).start();
    }, [focused, grow]);

    const hasBadge = badge != null && badge !== 0 && badge !== '';

    return (
        <Animated.View style={{flex: grow.interpolate({inputRange: [0, 1], outputRange: [1, 2.35]})}}>
            <Pressable
                // A tick only when the user actually moves to another tab.
                onPress={focused ? onPress : withHaptic(onPress, 'select')}
                onLongPress={onLongPress}
                accessibilityRole="tab"
                accessibilityState={{selected: focused}}
                accessibilityLabel={accessibilityLabel || label}
                style={styles.tabPress}
            >
                {focused ? (
                    <LinearGradient colors={BRAND.gradient} start={{x: 0, y: 0}} end={{x: 1, y: 1}} style={styles.activePill}>
                        {renderIcon('#FFFFFF')}
                        <Text style={styles.activeLabel} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.7} allowFontScaling={false}>{label}</Text>
                    </LinearGradient>
                ) : (
                    <View style={styles.idle}>
                        {renderIcon(inactiveColor)}
                    </View>
                )}
                {hasBadge && (
                    <View style={[styles.badge, focused && styles.badgeActive]}>
                        <Text style={styles.badgeText} numberOfLines={1}>
                            {typeof badge === 'number' && badge > 99 ? '99+' : badge}
                        </Text>
                    </View>
                )}
            </Pressable>
        </Animated.View>
    );
};

/**
 * Floating premium tab bar (React Navigation v6 `tabBar` renderer body).
 *
 * @param {object} props React Navigation `BottomTabBarProps` (`state`, `descriptors`, `navigation`).
 * @param {'dark'|'night'} [props.variant='dark'] `night` = the indigo driver canvas.
 * @returns {?JSX.Element} The bar, or `null` while the keyboard is open / a screen hides it.
 */
export const PremiumTabBar = ({state, descriptors, navigation, variant = 'dark'}) => {
    const m = useManagementTheme();
    const bottomInset = useBottomInset();
    const [keyboardVisible, setKeyboardVisible] = useState(false);

    useEffect(() => {
        const showEvent = Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow';
        const hideEvent = Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide';
        const show = Keyboard.addListener(showEvent, () => setKeyboardVisible(true));
        const hide = Keyboard.addListener(hideEvent, () => setKeyboardVisible(false));

        return () => {
            show.remove();
            hide.remove();
        };
    }, []);

    const focusedOptions = descriptors[state.routes[state.index]?.key]?.options || {};
    const flatStyle = StyleSheet.flatten(focusedOptions.tabBarStyle) || {};

    if (keyboardVisible || flatStyle.display === 'none') {
        return null;
    }

    const night = variant === 'night';

    return (
        <View pointerEvents="box-none" style={[styles.wrap, {bottom: PREMIUM_TAB_BAR_OFFSET + bottomInset}]}>
            <View
                style={[
                    styles.bar,
                    {backgroundColor: night ? NIGHT.card : m.colors.nav, borderColor: night ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.06)'},
                    m.shadow(3),
                    {shadowColor: '#050816', shadowOpacity: 0.34},
                ]}
            >
                {state.routes.map((route, index) => {
                    const {options} = descriptors[route.key];
                    const focused = state.index === index;
                    const label = resolveLabel(options, route.name);

                    const onPress = () => {
                        const event = navigation.emit({type: 'tabPress', target: route.key, canPreventDefault: true});

                        if (!focused && !event.defaultPrevented) {
                            navigation.navigate({name: route.name, merge: true});
                        }
                    };
                    const onLongPress = () => navigation.emit({type: 'tabLongPress', target: route.key});

                    return (
                        <TabItem
                            key={route.key}
                            focused={focused}
                            label={label}
                            badge={options.tabBarBadge}
                            accessibilityLabel={options.tabBarAccessibilityLabel}
                            inactiveColor={m.colors.navInactive}
                            renderIcon={(color) => (typeof options.tabBarIcon === 'function'
                                ? options.tabBarIcon({focused, color, size: 22})
                                : null)}
                            onPress={onPress}
                            onLongPress={onLongPress}
                        />
                    );
                })}
            </View>
        </View>
    );
};

/** Stable `tabBar` renderer for the standard dark pill. */
export const renderPremiumTabBar = (props) => <PremiumTabBar {...props} />;

/** Stable `tabBar` renderer for the indigo "night" pill (Sofőr app). */
export const renderNightTabBar = (props) => <PremiumTabBar {...props} variant="night" />;

const styles = StyleSheet.create({
    wrap: {position: 'absolute', left: 14, right: 14},
    bar: {
        flexDirection: 'row',
        alignItems: 'center',
        height: PREMIUM_TAB_BAR_HEIGHT,
        borderRadius: PREMIUM_TAB_BAR_HEIGHT / 2,
        paddingHorizontal: 8,
        borderWidth: 1,
        shadowOffset: {width: 0, height: 14},
        shadowRadius: 22,
        elevation: 14,
    },
    tabPress: {height: 52, alignItems: 'center', justifyContent: 'center'},
    idle: {height: 52, alignSelf: 'stretch', alignItems: 'center', justifyContent: 'center'},
    activePill: {
        height: 52,
        alignSelf: 'stretch',
        marginHorizontal: 2,
        borderRadius: 26,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 7,
        paddingHorizontal: 10,
    },
    activeLabel: {color: '#FFFFFF', fontSize: 13, fontWeight: '800', flexShrink: 1},
    badge: {
        position: 'absolute',
        top: 2,
        right: 6,
        minWidth: 18,
        height: 18,
        borderRadius: 9,
        paddingHorizontal: 4,
        backgroundColor: '#EF4444',
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 1.5,
        borderColor: '#1F2230',
    },
    badgeActive: {right: 2, top: -2},
    badgeText: {color: '#FFFFFF', fontSize: 10, fontWeight: '800', lineHeight: 13},
});

export default PremiumTabBar;
